import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  TextField, Grid, Stack, Switch, FormControlLabel, CircularProgress,
  Typography, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LinkIcon from '@mui/icons-material/Link';
import { toast } from 'react-toastify';
import { createPoojaType, updatePoojaType } from '../../api/endpoints/pooja';
import { uploadFile } from '../../api/endpoints/media';

const initialForm = {
  typeId: '', name: '', description: '', shortDescription: '',
  price: '', duration: '', category: '',
  features: '', gradient: 'from-[#c2185b] to-[#d6336c]',
  isActive: true, isFeatured: false, order: '0',
};

export default function AddPoojaModal({ open, onClose, onSaved, editData }) {
  const isEdit = !!editData;
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [useUrlInput, setUseUrlInput] = useState(false);

  useEffect(() => {
    if (open) {
      setUseUrlInput(!!editData?.image);
      if (editData) {
        setForm({
          typeId: editData.typeId || '',
          name: editData.name || '',
          description: editData.description || '',
          shortDescription: editData.shortDescription || '',
          price: editData.price || '',
          duration: editData.duration || '',
          category: editData.category || '',
          features: Array.isArray(editData.features) ? editData.features.join('\n') : (editData.features || ''),
          gradient: editData.gradient || 'from-[#c2185b] to-[#d6336c]',
          isActive: editData.isActive ?? true,
          isFeatured: editData.isFeatured ?? false,
          order: editData.order ?? '0',
        });
        const existingImage = editData.image || '';
        if (existingImage) {
          setImageUrl(existingImage);
          setImagePreview(existingImage);
        }
      } else {
        setForm(initialForm);
        setImageFile(null);
        setImagePreview(null);
        setImageUrl('');
      }
    }
  }, [open, editData]);

  const handleChange = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl('');
  };

  const handleSave = async () => {
    if (!form.name  || !form.price) {
      toast.error('Name, Type ID, and Price are required');
      return;
    }
    setSaving(true);
    try {
      let finalImageUrl = imageUrl || '';
      if (useUrlInput) {
        finalImageUrl = form.imageUrl || '';
      } else if (imageFile) {
        setImageUploading(true);
        const uploadRes = await uploadFile(imageFile);
        finalImageUrl = uploadRes.data.data?.url || '';
        setImageUploading(false);
      }
      const payload = {
        typeId: form.typeId,
        name: form.name,
        description: form.description,
        shortDescription: form.shortDescription || undefined,
        price: Number(form.price),
        duration: form.duration || undefined,
        category: form.category || undefined,
        features: form.features ? form.features.split('\n').map((f) => f.trim()).filter(Boolean) : [],
        gradient: form.gradient || undefined,
        isActive: form.isActive,
        isFeatured: form.isFeatured,
        order: Number(form.order) || 0,
        image: finalImageUrl || undefined,
      };
      if (isEdit) {
        await updatePoojaType(editData._id, payload);
        toast.success('Pooja type updated');
      } else {
        await createPoojaType(payload);
        toast.success('Pooja type created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save pooja type');
    } finally {
      setSaving(false);
      setImageUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>{isEdit ? 'Edit Pooja' : 'Add Pooja'}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* <Grid item xs={6}>
            <TextField label="Type ID (slug)" value={form.typeId} onChange={handleChange('typeId')} fullWidth required size="small" helperText="e.g. lakshmi-puja" InputProps={{ readOnly: isEdit }} />
          </Grid> */}
          <Grid item xs={6}>
            <TextField label="Name" value={form.name} onChange={handleChange('name')} fullWidth required size="small" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Price (₹)" type="number" value={form.price} onChange={handleChange('price')} fullWidth required size="small" />
          </Grid>
          <Grid item xs={3}>
            <TextField label="Duration" value={form.duration} onChange={handleChange('duration')} fullWidth size="small" placeholder="e.g. 2 hours" />
          </Grid>
          <Grid item xs={3}>
            <TextField label="Category" value={form.category} onChange={handleChange('category')} fullWidth size="small" placeholder="e.g. peace, prosperity" />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Order" type="number" value={form.order} onChange={handleChange('order')} fullWidth size="small" />
          </Grid>
          <Grid item xs={8}>
            <TextField label="Gradient class" value={form.gradient} onChange={handleChange('gradient')} fullWidth size="small" placeholder="from-[#c2185b] to-[#d6336c]" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Short Description" value={form.shortDescription} onChange={handleChange('shortDescription')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Description" value={form.description} onChange={handleChange('description')} fullWidth multiline rows={3} size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Features (one per line)" value={form.features} onChange={handleChange('features')} fullWidth multiline rows={4} size="small" placeholder="Feature 1&#10;Feature 2&#10;Feature 3" />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
              <FormControlLabel control={<Switch checked={form.isFeatured} onChange={(e) => setForm((p) => ({ ...p, isFeatured: e.target.checked }))} />} label="Featured" />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Pooja Image</Typography>
              <IconButton size="small" onClick={() => { setUseUrlInput(!useUrlInput); setImageFile(null); }} color={useUrlInput ? 'primary' : 'default'} title={useUrlInput ? 'Switch to file upload' : 'Enter image URL'}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Stack>
            {useUrlInput ? (
              <TextField label="Image URL" value={form.imageUrl || ''} onChange={(e) => setForm((p) => ({ ...p, imageUrl: e.target.value }))} fullWidth size="small" placeholder="https://example.com/image.jpg" helperText="Paste a direct image URL" />
            ) : (
              <>
                <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />} disabled={imageUploading}>
                  {imageUploading ? 'Uploading...' : (imagePreview ? 'Change Image' : 'Upload Image')}
                  <input type="file" hidden accept="image/*" onChange={handleImageSelect} />
                </Button>
                {imagePreview && (
                  <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                    <img src={imagePreview} alt="Preview" style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8, objectFit: 'cover' }} />
                    <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }} onClick={handleRemoveImage}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || imageUploading}>
          {saving ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update Pooja' : 'Create Pooja')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
